/**
 * The commission brief's rules, with no DOM and no network (plan §3.3).
 *
 * Everything that decides WHAT goes into the cart lives here, so node can prove
 * it offline (tests/wc-brief-core.test.mjs) and the W-A journey can assert the
 * same numbers in a browser. `wc-brief.js` is the element: it reads the form,
 * calls these, and talks to Horizon and the cart.
 *
 * Money is integer minor units (cents) in the market's presentment currency,
 * exactly as Liquid renders `variant.price` for the page's market.
 */

/**
 * Line-item property keys. CONSTANTS, never locale strings: Proofer, packing,
 * print and the daily "missing properties" count (plan §3.10) read these exact
 * keys, and Translate & Adapt must never be able to rename one. A rename after
 * Dani's answer (L-06) is one line here.
 *
 * A key starting with "_" is hidden from the customer at checkout and on the
 * order-status page; everything else is shown to them as written.
 */
export const PROP = Object.freeze({
  people: "People",
  pets: "Pets",
  recipient: "Recipient",
  occasion: "Occasion",
  subjects: "Subjects",
  notes: "Notes for your artist",
  text: "Text to print",
  font: "Font",
  photos: "Photos",
  photosLater: "Send photos later",
  artistName: "Artist",
  artist: "_artist",
  angelWings: "Angel wings",
  memorial: "_memorial",
  paymentChoice: "_payment_choice",
  briefId: "_brief_id",
  briefVersion: "_brief_version",
});

/** Bumped when the property set changes shape, so ops can tell the two apart. */
export const BRIEF_VERSION = "1";

/** The five steps, in order. Each is also its own `location.hash`. */
export const STEPS = Object.freeze(["who", "style", "size", "photos", "review"]);

/** `_payment_choice` values: machine-readable, and never shown to the customer. */
export const PAYMENT = Object.freeze({ upfront: "zero_upfront", now: "pay_now" });

/** The value the "Pay now" radio carries instead of a selling plan id. */
export const PAY_NOW = "now";

/** The artist value for "we'll match you". */
export const NEXT_AVAILABLE = "next-available";

/** Limits the counters and text fields enforce (and the tests assert). */
export const LIMITS = Object.freeze({ people: 12, pets: 6, textChars: 60, notesChars: 1500, nameChars: 40 });

/**
 * Option values are matched on a KEY derived from the name, never the name
 * itself: Translate & Adapt turns "Watercolor" into "Watercolour" in the UK
 * market, and the same value must still be recognised. Only used for meaning
 * (which finish is print-only); selection always goes by option value id.
 */
const SYNONYMS = {
  watercolour: "watercolor",
  "poster-only": "print",
  poster: "print",
  "print-only": "print",
  "unframed-print": "print",
};

/** @param {string} name */
export function optionKey(name) {
  const handle = String(name || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return SYNONYMS[handle] || handle;
}

/**
 * Whether an artist card's styles (its data-styles, space-separated style
 * keys) include the chosen style key. No chosen style yet: nobody is greyed.
 * @param {string | undefined} styles
 * @param {string} key
 */
export function paintsStyle(styles, key) {
  if (!key) return true;
  return String(styles || "")
    .split(/\s+/)
    .includes(key);
}

/**
 * The finish a Frame option value stands for. Anything that is neither
 * print-only nor canvas is a frame colour.
 * @param {string} name
 * @returns {'print' | 'canvas' | 'framed'}
 */
export function finishOf(name) {
  const key = optionKey(name);
  if (key === "print") return "print";
  if (key === "canvas") return "canvas";
  return "framed";
}

/**
 * "16×12" → "16 × 12 in", with centimetres where the market measures in them.
 * The option names come from the store, so anything that isn't two numbers is
 * shown as it is rather than guessed at.
 * @param {string} name
 * @param {boolean} [withCm]
 */
export function formatSize(name, withCm = false) {
  const match = String(name).match(/^\s*(\d+(?:\.\d+)?)\s*[x×X]\s*(\d+(?:\.\d+)?)\s*(?:in|")?\s*$/);
  if (!match) return String(name);
  const [, a, b] = match;
  const inches = `${a} × ${b} in`;
  if (!withCm) return inches;
  const cm = (n) => Math.round(Number(n) * 2.54);
  return `${inches} (${cm(a)} × ${cm(b)} cm)`;
}

/**
 * The market's money format, recovered from what Liquid's `money` filter
 * printed for 1111.11 in THIS market ("$1,111.11", "€1.111,11", "£1,111.11").
 * `shop.money_format` is the store's own (USD) format, and wraps it in a
 * <span>, so it is wrong on five of the six domains and unsafe as text. The
 * sample carries the market's symbol, separators and decimals; only the number
 * is swapped back for Shopify's placeholder, which Horizon's formatMoney reads.
 * @param {string} sample
 */
export function moneyFormatFromSample(sample) {
  const text = String(sample || "")
    .replace(/<[^>]*>/g, "")
    .trim();
  const patterns = [
    [/1,111\.11/, "{{amount}}"],
    [/1\.111,11/, "{{amount_with_comma_separator}}"],
    [/1[ \u00a0\u202f]111,11/, "{{amount_with_space_separator}}"],
    [/1[ \u00a0\u202f]111\.11/, "{{amount_with_period_and_space_separator}}"],
    [/1'111\.11/, "{{amount_with_apostrophe_separator}}"],
    [/1,111(?![.,\d])/, "{{amount_no_decimals}}"],
    [/1\.111(?![.,\d])/, "{{amount_no_decimals_with_comma_separator}}"],
    [/1111\.11/, "{{amount}}"],
  ];
  for (const [pattern, placeholder] of patterns) {
    if (pattern.test(text)) return text.replace(pattern, placeholder);
  }
  return "{{amount}}";
}

/**
 * The variant for a full set of option value ids, or null when that
 * combination does not exist (36 × 24 only comes as canvas, 10 × 8 never does).
 * @param {BriefData} data
 * @param {string[]} optionIds
 */
export function variantFor(data, optionIds) {
  const want = optionIds.map(String);
  return (
    data.variants.find((v) => v.options.length === want.length && v.options.every((id, i) => String(id) === want[i])) ||
    null
  );
}

/**
 * Whether a variant can be bought HERE: Shopify's availability, and not one
 * this market is excluded from (CA 18×24 framed; CA, AU and NZ 24×36 framed,
 * Luke 26 Sep). Markets cannot exclude one variant, so the data carries
 * `excluded` (snippets/wc-brief-data.liquid) and every choice and the Add ask
 * this one question, never `available` on its own.
 * @param {{ available: boolean, excluded?: boolean } | null | undefined} v
 */
export function purchasable(v) {
  return Boolean(v && v.available && !v.excluded);
}

/**
 * Every option value id at `position` that has at least one AVAILABLE variant
 * with the other positions fixed as given (null = any). Used to hide the
 * impossible combinations instead of greying them out.
 * @param {BriefData} data
 * @param {number} position - 0-based
 * @param {(string | null)[]} fixed
 */
export function availableValues(data, position, fixed) {
  const out = new Set();
  for (const v of data.variants) {
    if (!purchasable(v)) continue;
    const ok = fixed.every((id, i) => i === position || id == null || String(v.options[i]) === String(id));
    if (ok) out.add(String(v.options[position]));
  }
  return out;
}

/**
 * The cheapest available variant price for one option value, others fixed as
 * given. Drives "from $79" on the size cards.
 * @param {BriefData} data
 * @param {number} position
 * @param {string} valueId
 * @param {(string | null)[]} fixed
 */
export function fromPrice(data, position, valueId, fixed) {
  let best = null;
  for (const v of data.variants) {
    if (!purchasable(v) || String(v.options[position]) !== String(valueId)) continue;
    const ok = fixed.every((id, i) => i === position || id == null || String(v.options[i]) === String(id));
    if (ok && (best === null || v.price < best)) best = v.price;
  }
  return best;
}

/**
 * The combination to move to when one option changes: keep every other choice
 * if that variant exists and is available, otherwise keep as many as possible,
 * preferring the earlier options (style before size before frame). This is how
 * choosing 36 × 24 moves a framed choice to canvas instead of leaving the
 * shopper on a combination that does not exist.
 * @param {BriefData} data
 * @param {string[]} current
 * @param {number} position
 * @param {string} valueId
 * @returns {string[] | null}
 */
export function resolveCombination(data, current, position, valueId) {
  const wanted = current.map(String);
  wanted[position] = String(valueId);
  const exact = variantFor(data, wanted);
  if (purchasable(exact)) return wanted;

  let best = null;
  let bestScore = -1;
  for (const v of data.variants) {
    if (!purchasable(v) || String(v.options[position]) !== String(valueId)) continue;
    // Score: a kept earlier option outweighs every later one.
    let score = 0;
    v.options.forEach((id, i) => {
      if (i !== position && String(id) === wanted[i]) score += 2 ** (v.options.length - i);
    });
    if (score > bestScore) {
      best = v;
      bestScore = score;
    }
  }
  return best ? best.options.map(String) : null;
}

/**
 * Where the brief starts: the choice Horizon selected (`?variant=`, or the
 * first available) or a saved brief's, when this market can buy it. Otherwise
 * W2's rule, the same in both arms (markets-excluded handover, 26 Sep): the
 * SAME style and frame at the next size down; else the next size up in that
 * frame; else keep the style (resolveCombination's order). Sizes are ordered
 * as the product lists them. So a link to a variant this market does not
 * offer opens on one it does, never on a hidden card with an error.
 * @param {BriefData} data
 * @param {string[]} options
 * @returns {string[]}
 */
export function settleOptions(data, options) {
  if (purchasable(variantFor(data, options))) return options;
  const { style = data.styleIndex ?? 0, size = 1 } = data.positions || {};
  const order = (data.options?.[size]?.values || []).map((v) => String(v.id));
  const at = order.indexOf(String(options[size]));
  const sameFrame = order.filter((id) =>
    purchasable(
      variantFor(
        data,
        options.map((o, i) => (i === size ? id : String(o))),
      ),
    ),
  );
  const down = sameFrame.filter((id) => order.indexOf(id) < at).pop();
  const up = sameFrame.find((id) => order.indexOf(id) > at);
  const next = at >= 0 ? (down ?? up) : undefined;
  if (next) return options.map((o, i) => (i === size ? next : String(o)));
  return resolveCombination(data, options, style, options[style]) || options;
}

/**
 * How many subjects are charged as extras. The rule is DATA, per product line
 * (plan §3.3 "Extra subjects"; Tier 1 prices, Luke 26 Sep, pricing-and-offers §1):
 *  - studio: the line's base price covers an allowance (block setting
 *    `studio_included`: Family 3 people, Couple and Wedding 2, Memorial one
 *    of either, Pet its pet, a house or a place none), and every further person
 *    or pet is the extra-subject product's person or pet variant, n times;
 *  - legacy: the old Add Family / Add a Pet products, beyond what each line
 *    already included (block setting `legacy_included`, L-10);
 *  - none: nothing is charged per subject.
 * "either" is one subject of either kind and covers a PERSON first, the
 * dearer, so the shopper never pays more than the rule says. The charge is
 * always its own line, never folded into the variant price.
 * @param {BriefState} state
 * @param {BriefData} data
 * @returns {{ person: number, pet: number }}
 */
export function extraSubjectCounts(state, data) {
  const rule = data.extraSubject || { mode: "none" };
  if (rule.mode !== "studio" && rule.mode !== "legacy") return { person: 0, pet: 0 };
  // A studio rule without an allowance keeps the first contract: one subject.
  const included = rule.included || (rule.mode === "studio" ? { either: 1 } : {});
  let person = Math.max(0, clampInt(state.people, 0, LIMITS.people) - (included.people ?? 0));
  let pet = Math.max(0, clampInt(state.pets, 0, LIMITS.pets) - (included.pets ?? 0));
  let either = included.either ?? 0;
  const coveredPeople = Math.min(either, person);
  person -= coveredPeople;
  either -= coveredPeople;
  pet -= Math.min(either, pet);
  if (rule.mode === "legacy") return { person: rule.person ? person : 0, pet: rule.pet ? pet : 0 };
  return { person, pet };
}

/**
 * A choice's price label, against the variant chosen now: "+$20.00" when it
 * costs more, and its OWN price when it costs the same or less, never a
 * negative difference (Luke's audit A32, 14 Oct: "−$20.00" on Print only).
 * @param {number | null | undefined} price
 * @param {number} base - the chosen variant's price
 * @param {boolean} isCurrent
 * @param {(cents: number) => string} money
 */
export function priceLabel(price, base, isCurrent, money) {
  if (price == null) return "";
  return !isCurrent && price > base ? `+${money(price - base)}` : money(price);
}

/**
 * One progress segment's state. The bar fills up to the step the shopper is
 * ON (Luke's audit A19, 14 Oct: Back left later segments filled); a step
 * reached before and now ahead of them is "ahead", drawn like "todo" but still
 * a link, because route() lets a shopper return to it.
 * @param {number} i - the segment's step index
 * @param {number} current - the step on screen
 * @param {number} maxReached - the furthest step reached
 * @returns {'done' | 'current' | 'ahead' | 'todo'}
 */
export function progressState(i, current, maxReached) {
  if (i === current) return "current";
  if (i < current) return "done";
  return i <= maxReached ? "ahead" : "todo";
}

/**
 * What step 1 says the price covers, from the same rule: "Includes 3 people"
 * (Family), "Includes 2 people" (Couple, Wedding), "Includes 1 pet" (Pet),
 * "Includes 1 person or pet" (Memorial). Nothing on a house or a place, and
 * nothing before the cutover (legacy), so no Tier 1 wording shows on an
 * unswitched line.
 * @param {BriefData} data
 * @returns {{ key: 'people' | 'pets' | 'either', count: number } | null}
 */
export function includedNote(data) {
  const rule = data.extraSubject;
  if (rule?.mode !== "studio" || !rule.included) return null;
  const { people = 0, pets = 0, either = 0 } = rule.included;
  if (either > 0 && !people && !pets) return { key: "either", count: either };
  if (people > 0 && !pets && !either) return { key: "people", count: people };
  if (pets > 0 && !people && !either) return { key: "pets", count: pets };
  return null;
}

/**
 * Every cart line the brief will add, priced, in the order they are posted:
 * the portrait first, then the extra subjects, then the ticked extras.
 * @param {BriefState} state
 * @param {BriefData} data
 * @returns {BriefLine[]}
 */
export function buildLines(state, data) {
  /** @type {BriefLine[]} */
  const lines = [];
  const variant = variantFor(data, state.options);
  if (variant) {
    lines.push({
      role: "portrait",
      variantId: String(variant.id),
      quantity: 1,
      price: variant.price,
      plans: variant.plans || {},
    });
  }

  const counts = extraSubjectCounts(state, data);
  const rule = data.extraSubject || { mode: "none" };
  // Studio: a missing pet line never makes a pet free; it is charged as a person.
  const petLine = rule.mode === "studio" ? rule.pet || rule.person : rule.pet;
  if (rule.person && counts.person > 0) lines.push(extraLine("extra_person", rule.person, counts.person));
  if (petLine && counts.pet > 0) lines.push(extraLine("extra_pet", petLine, counts.pet));

  if (state.memorial && state.angelWings && data.angelWingsLine) {
    lines.push(extraLine("angel_wings", data.angelWingsLine, 1));
  }

  for (const extra of data.extras || []) {
    if (!state.extras?.[extra.key]) continue;
    if (state.memorial && !extra.memorialOk) continue;
    if (!extra.available) continue;
    lines.push(extraLine(`extra_${extra.key}`, extra, 1));
  }
  return lines;
}

/**
 * @param {string} role
 * @param {{ variantId: string | number, price: number, plans?: Record<string, number> }} source
 * @param {number} quantity
 * @returns {BriefLine}
 */
function extraLine(role, source, quantity) {
  return { role, variantId: String(source.variantId), quantity, price: source.price, plans: source.plans || {} };
}

/**
 * Whether a line is sold on the chosen plan. The portrait always is when a plan
 * is chosen and its variant carries it; the other lines only when
 * `extrasJoinPlan` (Dani and Luke decide, L-10) and their product carries the
 * same plan.
 * @param {BriefLine} line
 * @param {string} planId
 * @param {BriefData} data
 */
export function lineTakesPlan(line, planId, data) {
  if (!planId || planId === PAY_NOW) return false;
  if (!(planId in (line.plans || {}))) return false;
  return line.role === "portrait" || Boolean(data.extrasJoinPlan);
}

/**
 * The running total and what is charged today. "Due today" is each plan line's
 * `selling_plan_allocation.checkout_charge_amount` plus the full price of every
 * line without a plan (plan §3.3); W-A asserts that checkout shows the same.
 * @param {BriefLine[]} lines
 * @param {string} planId
 * @param {BriefData} data
 */
export function totals(lines, planId, data) {
  let total = 0;
  let due = 0;
  for (const line of lines) {
    const full = line.price * line.quantity;
    total += full;
    due += lineTakesPlan(line, planId, data) ? line.plans[planId] * line.quantity : full;
  }
  return { total, due, later: total - due };
}

/**
 * The portrait's properties. Only keys from PROP, plus the upload app's own
 * fields (passed in already allow-listed by where they sit in the form), ever
 * reach the cart: anything a ScriptTag app injects elsewhere is dropped.
 * @param {BriefState} state
 * @param {BriefData} data
 * @param {[string, string][]} uploadEntries - `[key, value]`, key without `properties[...]`
 * @returns {Record<string, string>}
 */
export function portraitProperties(state, data, uploadEntries = []) {
  /** @type {Record<string, string>} */
  const p = {};
  const people = clampInt(state.people, 0, LIMITS.people);
  const pets = clampInt(state.pets, 0, LIMITS.pets);
  if (people > 0) p[PROP.people] = String(people);
  if (pets > 0) p[PROP.pets] = String(pets);
  if (clean(state.recipient)) p[PROP.recipient] = clean(state.recipient);
  if (clean(state.occasion)) p[PROP.occasion] = clean(state.occasion);

  // A33 (Luke's audit, 14 Oct): who is in EACH uploaded photo, one line per
  // photo in upload order, and Subjects becomes the names given, each once.
  // Only for photos sent now; a photo with no names writes no line.
  const perPhoto =
    state.photos === "now"
      ? (state.photoPeople || []).map((names) =>
          (names || []).map((n) => clean(n).slice(0, LIMITS.nameChars)).filter(Boolean),
        )
      : [];
  perPhoto.forEach((names, i) => {
    if (names.length) p[`Who's in photo ${i + 1}`] = names.join(", ");
  });
  const named = [...new Set(perPhoto.flat())];
  const subjects = named.length ? named : subjectLabels(state, data);
  if (subjects.length) p[PROP.subjects] = subjects.join(", ");

  // The upload app's fields, minus anything claiming one of the brief's own
  // keys: those are the brief's to write, whoever else sits in the form.
  const briefKeys = new Set(Object.values(PROP));
  const uploads = uploadEntries.filter(([key]) => !briefKeys.has(key));

  if (state.photos === "later") {
    p[PROP.photosLater] = "Yes";
  } else {
    // One key per upload is the app's; this is the count, so an order with
    // photos can be told from one without them at a glance. The element passes
    // `photoCount` when it can see the files; otherwise the app's visible,
    // non-empty fields are counted (its "_" keys are bookkeeping, not photos).
    // A photo the upload app lists on screen (one A33 group each) is uploaded,
    // whatever its hidden field reads.
    const count = Math.max(
      state.photoCount ?? uploads.filter(([key, value]) => !key.startsWith("_") && clean(value)).length,
      (state.photoPeople || []).length,
    );
    if (count > 0) p[PROP.photos] = String(count);
    // "Add photos now" with no photo counted is chased as Send photos later
    // (DA1's review, 10 Oct): with neither key, nothing on the order says
    // photos are owed and no chase is sent. validate() still stops an empty
    // "now" where it can see the uploads; this covers where it cannot.
    else p[PROP.photosLater] = "Yes";
  }

  if (clean(state.notes)) p[PROP.notes] = clean(state.notes).slice(0, LIMITS.notesChars);

  const text = textToPrint(state);
  if (text) {
    p[PROP.text] = text;
    if (clean(state.font)) p[PROP.font] = clean(state.font);
  }

  // "Artist" is a contract (Luke to Dani, 11 Oct): Nick's Proofer reads it
  // from the order to assign that artist and tag the order, and assigns at
  // random when it is ABSENT. So it is written only for a chosen artist, as
  // the first name shown (never empty: Shopify drops an empty property), and
  // never for "Next available". It replaced "Artist request" on 11 Oct.
  const artist = (data.artists || []).find((a) => a.handle === state.artist);
  if (artist) p[PROP.artistName] = clean(artist.firstName) || artist.handle;
  p[PROP.artist] = artist ? artist.handle : NEXT_AVAILABLE;

  if (state.memorial) {
    p[PROP.memorial] = "true";
    if (state.angelWings) p[PROP.angelWings] = "Yes";
  }

  p[PROP.paymentChoice] = state.payment && state.payment !== PAY_NOW ? PAYMENT.upfront : PAYMENT.now;
  p[PROP.briefId] = state.briefId;
  p[PROP.briefVersion] = BRIEF_VERSION;

  for (const [key, value] of uploads) p[key] = value;
  return p;
}

/**
 * The text to print, or '' when there is none. Memorials ask for the name and
 * the dates separately (plan §3.3 step 4) and they are joined the way they
 * would be set on the print.
 * @param {BriefState} state
 */
export function textToPrint(state) {
  if (!state.addText) return "";
  if (state.memorial) {
    return [clean(state.textName), clean(state.textDates)]
      .filter(Boolean)
      .join(" · ")
      .slice(0, LIMITS.textChars * 2);
  }
  return clean(state.textLine).slice(0, LIMITS.textChars);
}

/**
 * The labels the subjects go by: what the shopper typed, or "Person 2" / "Pet 1".
 * @param {BriefState} state
 * @param {BriefData} data
 */
export function subjectLabels(state, data) {
  const people = clampInt(state.people, 0, LIMITS.people);
  const pets = clampInt(state.pets, 0, LIMITS.pets);
  const names = state.subjects || [];
  const labels = [];
  const personWord = data.labels?.person || "Person";
  const petWord = data.labels?.pet || "Pet";
  for (let i = 0; i < people; i++) labels.push(clean(names[i]).slice(0, LIMITS.nameChars) || `${personWord} ${i + 1}`);
  for (let i = 0; i < pets; i++)
    labels.push(clean(names[people + i]).slice(0, LIMITS.nameChars) || `${petWord} ${i + 1}`);
  return labels;
}

/**
 * The items[] for one `POST /cart/add.js`. Every line carries `_brief_id` so
 * the drawer can group them and ops can join them back to the portrait.
 * `selling_plan` is sent only where the line takes the plan.
 *
 * In REVERSE of buildLines, the portrait last: Shopify lists a cart newest
 * first, so the portrait then heads the drawer, checkout and the order Dani
 * packs from, with the extras under it in the review's own order (I-53).
 * @param {BriefState} state
 * @param {BriefData} data
 * @param {[string, string][]} uploadEntries
 */
export function buildItems(state, data, uploadEntries = []) {
  const lines = buildLines(state, data);
  return lines
    .map((line) => {
      /** @type {{ id: number, quantity: number, properties: Record<string, string>, selling_plan?: number }} */
      const item = {
        id: Number(line.variantId),
        quantity: line.quantity,
        properties:
          line.role === "portrait"
            ? portraitProperties(state, data, uploadEntries)
            : { [PROP.briefId]: state.briefId, ...(state.memorial ? { [PROP.memorial]: "true" } : {}) },
      };
      if (lineTakesPlan(line, state.payment, data)) item.selling_plan = Number(state.payment);
      return item;
    })
    .reverse();
}

/**
 * Per-step validation. Returns error codes with the field they belong to; the
 * element turns codes into words. Nothing here relies on `required`: a hidden
 * required field blocks a native submit with no message the shopper can see.
 * @param {string} step
 * @param {BriefState} state
 * @param {BriefData} data
 * @param {{ uploadsDetectable?: boolean, uploadCount?: number }} [uploads]
 * @returns {{ field: string, code: string }[]}
 */
export function validateStep(step, state, data, uploads = {}) {
  /** @type {{ field: string, code: string }[]} */
  const errors = [];
  const people = clampInt(state.people, 0, LIMITS.people);
  const pets = clampInt(state.pets, 0, LIMITS.pets);

  switch (step) {
    case "who":
      if (people + pets < (data.minSubjects ?? 1)) errors.push({ field: "people", code: "subjects_min" });
      break;
    case "style":
      if (!state.options?.[data.styleIndex ?? 0]) errors.push({ field: "style", code: "style_missing" });
      // The words to print sit beneath the style cards (Luke's audit A04).
      if (state.addText && !textToPrint(state)) errors.push({ field: "text", code: "text_empty" });
      if (state.addText && !state.memorial && clean(state.textLine).length > LIMITS.textChars) {
        errors.push({ field: "text", code: "text_long" });
      }
      break;
    case "size": {
      const variant = variantFor(data, state.options || []);
      if (!variant) errors.push({ field: "size", code: "combination_missing" });
      else if (!purchasable(variant)) errors.push({ field: "size", code: "variant_unavailable" });
      break;
    }
    case "photos": {
      if (state.photos !== "now" && state.photos !== "later") {
        errors.push({ field: "photos", code: "photos_choice" });
      } else if (state.photos === "now" && uploads.uploadsDetectable) {
        const expected = Math.max(1, Math.min(people + pets, data.maxUploadFields ?? 12));
        if ((uploads.uploadCount ?? 0) < 1) errors.push({ field: "photos", code: "photos_missing" });
        else if ((uploads.uploadCount ?? 0) < expected && data.onePhotoPerSubject) {
          errors.push({ field: "photos", code: "photos_some_missing" });
        }
      }
      if (clean(state.notes).length > LIMITS.notesChars) errors.push({ field: "notes", code: "notes_long" });
      break;
    }
    case "review":
      if (!state.payment) errors.push({ field: "payment", code: "payment_missing" });
      break;
    default:
      break;
  }
  return errors;
}

/**
 * The first step with an error, for an Add pressed before the brief is done:
 * the shopper is taken there rather than shown a disabled button (the kit's
 * commerce check also needs `name="add"` present and enabled at load).
 * @param {BriefState} state
 * @param {BriefData} data
 * @param {{ uploadsDetectable?: boolean, uploadCount?: number }} [uploads]
 */
export function firstInvalidStep(state, data, uploads = {}) {
  for (const step of STEPS) {
    const errors = validateStep(step, state, data, uploads);
    if (errors.length) return { step, errors };
  }
  return null;
}

/**
 * The autosave key: one brief per product per market, because prices, spelling
 * and plans differ by market and a brief started on .com must not reappear,
 * priced in dollars, on .co.uk.
 * @param {string} market
 * @param {string} handle
 */
export function autosaveKey(market, handle) {
  return `wc-brief:v${BRIEF_VERSION}:${market}:${handle}`;
}

/** Saved briefs older than this are not offered back. */
export const AUTOSAVE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * A short, unguessable id for `_brief_id`. Not a secret; it only has to be
 * unique enough to join a cart's lines back together.
 * @param {() => number} [random]
 */
export function newBriefId(random = Math.random) {
  const alphabet = "abcdefghijkmnpqrstuvwxyz23456789";
  let id = "";
  for (let i = 0; i < 12; i++) id += alphabet[Math.floor(random() * alphabet.length)];
  return `b_${id}`;
}

/**
 * The coded return link (I-58). "Email me my brief" links back to
 * `#<step>;<code>`: in the hash, which a mail client's link tracking passes
 * through untouched and which never goes over the wire. Scripts DO read it:
 * Shopify's analytics, Clarity and Triple Whale send location.href whole, so
 * snippets/wc-head-early.liquid takes the code out before any of them runs.
 *
 * The code carries CHOICES ONLY: counts, chip positions, switches, the
 * artist's handle, the extras and the plan. Never a name, a note, text to
 * print, a font, an email or a photo, because a link is forwarded and read
 * over shoulders. A chip is carried by its POSITION, not its label, so a code
 * written on .com reads the same on .co.uk, where the labels differ. The
 * style, size and frame travel as Shopify's own `?variant=`.
 *
 *   1.p2.t1.r0.o3.m.w.a<handle>.x3.n.l
 *   version . people . pets . recipient . occasion . memorial . wings .
 *   artist . extras (bits) . pay now . photos later
 *
 * `c` was Combine Photos, retired on 26 Sep. The version stays 1: decode
 * ignores a token it does not know, so a link emailed before then still
 * opens, without Combine.
 */
export const LINK_VERSION = "1";
const EXTRA_BITS = Object.freeze({ priority: 1, digital: 2, protection: 4 });
const HANDLE = /^[a-z0-9-]{1,60}$/;
const COUNT = /^\d{1,3}$/;

/** @param {LinkChoices} c */
export function encodeChoices(c) {
  const parts = [LINK_VERSION];
  const count = (/** @type {unknown} */ v, /** @type {number} */ max) =>
    COUNT.test(String(v ?? "").trim()) ? clampInt(v, 0, max) : null;
  const people = count(c.people, LIMITS.people);
  const pets = count(c.pets, LIMITS.pets);
  const position = (/** @type {unknown} */ i) => (Number.isInteger(i) && Number(i) >= 0 ? i : null);
  if (people != null) parts.push(`p${people}`);
  if (pets != null) parts.push(`t${pets}`);
  if (position(c.recipient) != null) parts.push(`r${c.recipient}`);
  if (position(c.occasion) != null) parts.push(`o${c.occasion}`);
  if (c.memorial) parts.push("m");
  if (c.angelWings) parts.push("w");
  if (c.artist && c.artist !== NEXT_AVAILABLE && HANDLE.test(c.artist)) parts.push(`a${c.artist}`);
  let bits = 0;
  for (const [key, bit] of Object.entries(EXTRA_BITS)) if (c.extras?.[key]) bits |= bit;
  if (bits) parts.push(`x${bits}`);
  if (c.payNow) parts.push("n");
  if (c.photosLater) parts.push("l");
  return parts.join(".");
}

/**
 * The code back into choices, judged against what THIS page offers. Each
 * field stands or falls on its own, so one tampered token costs only itself;
 * an unknown version is refused whole, because its fields may mean something
 * else. Null when nothing usable is left.
 * @param {unknown} code
 * @param {LinkOffer} offer
 * @returns {LinkChoices | null}
 */
export function decodeChoices(code, offer) {
  if (typeof code !== "string" || code.length > 200) return null;
  const [version, ...tokens] = code.split(".");
  if (version !== LINK_VERSION) return null;
  /** @type {LinkChoices} */
  const out = {};
  const seen = new Set();
  let bits = 0;
  for (const token of tokens) {
    const key = token.charAt(0);
    const rest = token.slice(1);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const n = COUNT.test(rest) ? Number(rest) : NaN;
    const flag = rest === "";
    if (key === "p" && n >= 0) out.people = Math.min(n, LIMITS.people);
    else if (key === "t" && n >= 0) out.pets = Math.min(n, LIMITS.pets);
    else if (key === "r" && n < offer.recipients) out.recipient = n;
    else if (key === "o" && n < offer.occasions) out.occasion = n;
    else if (key === "m" && flag) out.memorial = true;
    else if (key === "w" && flag) out.angelWings = true;
    else if (key === "a" && (offer.artists || []).includes(rest)) out.artist = rest;
    else if (key === "x" && n > 0 && n <= 7) bits = n;
    else if (key === "n" && flag) out.payNow = true;
    else if (key === "l" && flag) out.photosLater = true;
  }
  const memorial = Boolean(out.memorial || offer.memorialProduct);
  // What the brief itself shows: a memorial has no occasion and takes only
  // the extras marked for it; wings are a memorial's alone.
  if (memorial) delete out.occasion;
  else delete out.angelWings;
  /** @type {Record<string, boolean>} */
  const extras = {};
  for (const extra of offer.extras || []) {
    const bit = EXTRA_BITS[/** @type {keyof typeof EXTRA_BITS} */ (extra.key)];
    if (bit && bits & bit && (!memorial || extra.memorialOk)) extras[extra.key] = true;
  }
  if (Object.keys(extras).length) out.extras = extras;
  return Object.keys(out).length ? out : null;
}

/** @param {unknown} value */
function clean(value) {
  return String(value ?? "")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")
    .trim();
}

/**
 * @param {unknown} value
 * @param {number} min
 * @param {number} max
 */
export function clampInt(value, min, max) {
  const n = Number.parseInt(String(value ?? ""), 10);
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
}

/**
 * @typedef {object} BriefLine
 * @property {string} role
 * @property {string} variantId
 * @property {number} quantity
 * @property {number} price - cents, one unit
 * @property {Record<string, number>} plans - selling plan id → checkout charge, cents
 *
 * @typedef {object} BriefData
 * @property {{ id: string | number, available: boolean, excluded?: boolean, price: number, options: (string|number)[], plans?: Record<string, number> }[]} variants - `excluded`: this market does not offer it
 * @property {{ mode: 'studio' | 'legacy' | 'none', included?: { people?: number, pets?: number, either?: number }, person?: any, pet?: any }} [extraSubject]
 * @property {{ key: string, variantId: string | number, price: number, available: boolean, memorialOk?: boolean, plans?: Record<string, number> }[]} [extras]
 * @property {{ variantId: string | number, price: number, plans?: Record<string, number> } | null} [angelWingsLine]
 * @property {boolean} [extrasJoinPlan]
 * @property {{ handle: string, firstName: string }[]} [artists]
 * @property {number} [minSubjects]
 * @property {number} [styleIndex]
 * @property {number} [maxUploadFields]
 * @property {boolean} [onePhotoPerSubject]
 * @property {string} [nextAvailableLabel]
 * @property {{ person?: string, pet?: string }} [labels]
 *
 * @typedef {object} BriefState
 * @property {string} briefId
 * @property {string[]} options - option value ids, in option order
 * @property {number | string} people
 * @property {number | string} pets
 * @property {string} [recipient]
 * @property {string} [occasion]
 * @property {boolean} [memorial]
 * @property {string} [artist]
 * @property {string[]} [subjects]
 * @property {'now' | 'later' | ''} [photos]
 * @property {string} [notes]
 * @property {boolean} [addText]
 * @property {string} [textLine]
 * @property {string} [textName]
 * @property {string} [textDates]
 * @property {string} [font]
 * @property {boolean} [angelWings]
 * @property {Record<string, boolean>} [extras]
 * @property {string} [payment] - a selling plan id, or PAY_NOW
 * @property {number} [photoCount] - files the upload app holds, when the element can count them
 *
 * @typedef {object} LinkChoices - what a coded return link may carry (I-58)
 * @property {number | string} [people]
 * @property {number | string} [pets]
 * @property {number} [recipient] - the chip's position
 * @property {number} [occasion] - the chip's position
 * @property {boolean} [memorial]
 * @property {boolean} [angelWings]
 * @property {string} [artist] - a handle; absent is next available
 * @property {Record<string, boolean>} [extras]
 * @property {boolean} [payNow]
 * @property {boolean} [photosLater]
 *
 * @typedef {object} LinkOffer - what this page offers, to judge a code against
 * @property {number} recipients
 * @property {number} occasions
 * @property {string[]} artists
 * @property {{ key: string, memorialOk?: boolean }[]} extras
 * @property {boolean} [memorialProduct]
 */
